import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { ActivatedRoute } from '@angular/router';

import { PortalApiService } from '../../portal-api.service';
import { PortalAuthService } from '../../portal-auth.service';
import { PortalPreviewService } from '../../portal-preview.service';
import { portalDate } from '../../shared/portal-format';

type InvoiceFilter = 'all' | 'unpaid' | 'paid';

@Component({
	selector: 'portal-invoices',
	templateUrl: './invoices.component.html',
	styleUrls: ['./invoices.component.scss']
})
export class InvoicesComponent implements OnInit, OnDestroy {

	private sub?: Subscription;

	projectId!: string;
	loading = true;
	error = '';

	invoices: any[] = [];
	sections: string[] = [];
	counts = { all: 0, unpaid: 0, paid: 0 };

	filter: InvoiceFilter = 'all';
	search = '';

	constructor(
		private route: ActivatedRoute,
		private api: PortalApiService,
		private auth: PortalAuthService,
		public preview: PortalPreviewService
	) {}

	get filtered(): any[] {
		const q = this.search.trim().toLowerCase();
		return this.invoices.filter(inv => {
			if (this.filter !== 'all' && inv.status !== this.filter) return false;
			if (!q) return true;
			return (`${inv.invoice_no} ${inv.description}`).toLowerCase().includes(q);
		});
	}

	ngOnInit(): void {
		// Subscribed rather than read once from the snapshot: Angular reuses this
		// component when only the :id changes, so switching project (from the
		// activity feed, or with the browser's back button) would otherwise leave
		// the previous project's content on screen.
		this.sub = this.route.params.subscribe(params => {
			this.projectId = params['id'] || '';
			this.load();
		});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}

	load(): void {
		this.loading = true;
		this.error = '';
		this.api.project.invoices(this.projectId, (res: any) => {
			this.invoices = res?.invoices || [];
			this.sections = res?.sections || [];
			this.counts = res?.counts || this.counts;
			this.loading = false;
		}, (err: any) => {
			this.loading = false;
			if (err?.status === 401) { this.auth.goToLogin(); return; }
			this.error = err?.message || 'Could not load invoices.';
		});
	}

	pctOf(value: number, total: number): number {
		if (!total) return 0;
		return Math.min(100, Math.max(0, Math.round((value / total) * 100)));
	}

	// Open the invoice's public web view in a new tab. invoice_url is an absolute
	// main-app URL; null when the invoice has no secure token.
	openInvoice(inv: any): void {
		if (inv?.invoice_url) window.open(inv.invoice_url, '_blank', 'noopener');
	}

	// Open the invoice in a new tab with the payment flow auto-opened. pay_url is
	// null when the invoice is paid or the org can't take online payments.
	payInvoice(inv: any): void {
		if (inv?.pay_url) window.open(inv.pay_url, '_blank', 'noopener');
	}

	dueLabel(inv: any): string {
		if (inv.status === 'paid') return 'Paid';
		return inv.due_date ? `Due: ${portalDate(inv.due_date)}` : 'Due';
	}

}
